import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { SignupForm } from '@/components/auth/SignupForm';
import { redirectIfSignedIn } from '@/lib/use-session';
import {
  isValidReferralCode,
  storeSignupRef,
} from '@/lib/signup-ref-cookie';

type SignupSearch = {
  // Clerk-issued ticket on invited signups. The Clerk Backend API returns a
  // URL of the form `${redirectUrl}?__clerk_ticket=…` when an invitation is
  // created with `notify=false`; landing here with that param means the
  // user is accepting a directory or admin invite.
  __clerk_ticket?: string;
  // Group-invite token forwarded from `/invites/:token` when a signed-out
  // visitor lands on a redeem URL. Stashed into Clerk's `unsafeMetadata`
  // during `signUp.create()` so the server's `user.created` webhook can
  // auto-redeem it after Clerk provisions the account.
  pendingInviteToken?: string;
  tenant?: string;
  invitation?: string;
  // Referral tracking code. Attributes the resulting signup to an admin's
  // referral link; grants nothing. Stashed into a first-party cookie on
  // arrival so it survives the OAuth round-trip, then claimed at /auth/finishing.
  ref?: string;
};

export const Route = createFileRoute('/auth/signup')({
  beforeLoad: ({ context }) => redirectIfSignedIn(context),
  validateSearch: (search: Record<string, unknown>): SignupSearch => {
    const ticket = search['__clerk_ticket'];
    const pendingInviteToken = search['pendingInviteToken'];
    const t = search['tenant'];
    const i = search['invitation'];
    const ref = search['ref'];
    return {
      ...(typeof ticket === 'string' && ticket.length > 0
        ? { __clerk_ticket: ticket }
        : {}),
      ...(typeof pendingInviteToken === 'string' && pendingInviteToken.length > 0
        ? { pendingInviteToken }
        : {}),
      ...(typeof t === 'string' && t.length > 0 ? { tenant: t } : {}),
      ...(typeof i === 'string' && i.length > 0 ? { invitation: i } : {}),
      ...(typeof ref === 'string' && isValidReferralCode(ref) ? { ref } : {}),
    };
  },
  component: SignupPage,
});

function SignupPage() {
  const t = useTranslations('Auth');
  const { __clerk_ticket: ticket, pendingInviteToken } = Route.useSearch();
  const { tenant, invitation, ref } = Route.useSearch();
  // Persist the referral code before the OAuth round-trip leaves this origin.
  // Validated in validateSearch, re-checked inside storeSignupRef.
  useEffect(() => {
    if (ref !== undefined) storeSignupRef(ref);
  }, [ref]);
  return (
    <AuthLayout headline={t('layout.promoHeadline')}>
      <SignupForm
        {...(ticket !== undefined ? { invitationTicket: ticket } : {})}
        {...(pendingInviteToken !== undefined
          ? { pendingInviteToken }
          : {})}
        {...(tenant !== undefined ? { initialTenantCode: tenant } : {})}
        {...(invitation !== undefined ? { initialInvitationId: invitation } : {})}
      />
    </AuthLayout>
  );
}
